import { onUnmounted, ref, type Ref } from 'vue'

/**
 * Tailwind's own breakpoints, so a decision made in markup and one made with
 * an `sm:` or `lg:` utility flip at the same width rather than a few pixels
 * apart.
 *
 * Prefer the utilities wherever plain styling will do. Reach for this only
 * when the markup itself has to differ — a different component, a different
 * number of columns fetched, a drawer that must be closed on widening.
 */
export function useMediaQuery(query: string): Ref<boolean> {
  const matches = ref(false)
  if (typeof window === 'undefined' || !window.matchMedia) return matches

  const mql = window.matchMedia(query)
  matches.value = mql.matches

  const onChange = (event: MediaQueryListEvent) => {
    matches.value = event.matches
  }
  mql.addEventListener('change', onChange)
  onUnmounted(() => mql.removeEventListener('change', onChange))

  return matches
}

/** A phone. Below Tailwind's `sm`. */
export const useIsPhone = () => useMediaQuery('(max-width: 639px)')

/**
 * A phone or a tablet: anything that gets the bottom bar rather than the
 * sidebar. Below Tailwind's `lg`.
 */
export const useIsCompact = () => useMediaQuery('(max-width: 1023px)')
